/**
 * The only script the browser gets.
 *
 * It wires two things and nothing else: the appearance toggle, and a clipboard fallback for
 * the deletion page's email address. Everything a visitor reads is already in the HTML.
 *
 * Both halves **fail closed**. An unreachable `localStorage` — a private window, a browser
 * set to block site data, an iframe with no storage access — throws on *access*, not on read,
 * so every touch is wrapped. When it throws, nothing is stored, no attribute is set, and
 * `prefers-color-scheme` decides. That is the right answer rather than a broken one, which is
 * the same rule `Site/`'s inline theme script follows.
 */
import '../styles/app.css';

type ThemePreference = 'system' | 'light' | 'dark';

/** The same versioned key `Site/` uses, so the two never need reconciling if they ever share
 * an origin. Storage is per-origin, so today they are simply the same shape. */
const STORAGE_KEY = 'concentric.prefs.v1';

function readTheme(): ThemePreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 'system';
    const parsed: unknown = JSON.parse(raw);
    const theme =
      typeof parsed === 'object' && parsed !== null
        ? (parsed as { theme?: unknown }).theme
        : undefined;
    return theme === 'light' || theme === 'dark' ? theme : 'system';
  } catch {
    return 'system';
  }
}

function writeTheme(theme: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ theme }));
  } catch {
    /* The toggle still works for this page load; it just will not be remembered. */
  }
}

/**
 * `system` **removes** the attribute rather than setting it to `'system'`.
 *
 * The stylesheet's dark block is `@media (prefers-color-scheme: dark) :root:not([data-theme='light'])`,
 * so any value at all in the attribute is fine for dark and only the *absence* of the
 * attribute lets the media query decide. Writing `data-theme="system"` would look correct and
 * would pin the page to light on a machine set to dark.
 */
function applyTheme(theme: ThemePreference): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);

  for (const button of document.querySelectorAll<HTMLButtonElement>('[data-theme-option]')) {
    button.setAttribute('aria-pressed', String(button.dataset.themeOption === theme));
  }
}

function wireThemeToggle(): void {
  applyTheme(readTheme());
  for (const button of document.querySelectorAll<HTMLButtonElement>('[data-theme-option]')) {
    button.addEventListener('click', () => {
      const chosen = button.dataset.themeOption;
      if (chosen !== 'system' && chosen !== 'light' && chosen !== 'dark') return;
      writeTheme(chosen);
      applyTheme(chosen);
    });
  }
}

/**
 * The copy-address button on the deletion page is rendered `hidden`, and is revealed only if
 * there is a clipboard to attach it to. A button that silently does nothing on the page
 * somebody reached because they are already annoyed is worse than no button.
 */
function wireCopyButtons(): void {
  if (!navigator.clipboard) return;
  for (const button of document.querySelectorAll<HTMLButtonElement>('[data-copy]')) {
    const value = button.dataset.copy;
    if (!value) continue;
    button.hidden = false;
    button.addEventListener('click', () => {
      void navigator.clipboard.writeText(value).then(
        () => {
          const original = button.textContent;
          button.textContent = 'Copied';
          window.setTimeout(() => {
            button.textContent = original;
          }, 2000);
        },
        () => {
          /* Denied by permissions policy. The address is on screen either way. */
        },
      );
    });
  }
}

wireThemeToggle();
wireCopyButtons();
